import NavTabs from 'shared/components/NavTabs/NavTabs';
import {
  Outlet,
  useNavigate,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import routes from 'router/routes';
import { AccessRoles } from 'shared/auth';
import { isAllowed, useAuthContext } from 'context/AuthContext';
import { useEffect, useMemo, useRef } from 'react';
import { Paper, Stack, styled } from '@mui/material';
import { Path } from 'shared/types';
import usePlanStore from 'store/planStore';

const Wrapper = styled(Stack)(({ theme }) => ({
  minHeight: 'calc(100% - 74px)',
  backgroundColor: theme.palette.lightGrey.main,
}));

const Content = styled(Paper)({
  borderRadius: 0,
  display: 'flex',
  flexDirection: 'column',
  margin: '0',
  flexGrow: 1,
  boxShadow: 'none',
});

const resetPlanStore = () => {
  const s = usePlanStore.getState();
  s.setIsPlanCreating(false);
  s.setSelectedCountry(null);
  s.resetGeneralInfoState();
  s.resetSavedGeneralInfoState();
  s.resetCountryForms();
  s.resetAllSavedCountryForms();
  s.resetAllStoredCountryForms();
  s.setAlreadySavedPlanId();
  s.setActiveTab('generalInfo');
};

function Calculator(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const navType = useNavigationType();
  const { userRoles } = useAuthContext();
  const { t } = useTranslation();
  const { isPlanCreating } = usePlanStore();

  const handledPopRef = useRef(false);

  const filteredPaths = useMemo<Path[]>(
    () =>
      [
        {
          to: routes.mediaPlanning,
          label: t('mediaplanning'),
          show: isAllowed(
            [AccessRoles.FULL_ACCESS, AccessRoles.ONLINE_CAMPAIGNS],
            userRoles
          ),
        },
        {
          to: routes.fairValue,
          label: t('calculator.fairValue'),
          show: isAllowed(
            [AccessRoles.FULL_ACCESS, AccessRoles.FAIR_VALUE],
            userRoles
          ),
        },
        {
          to: routes.digitalFairValue,
          label: t('calculator.digitalFairValue'),
          show: isAllowed(
            [AccessRoles.FULL_ACCESS, AccessRoles.FAIR_VALUE],
            userRoles
          ),
        },
      ].filter(({ show }) => show),
    [t, userRoles]
  );

  useEffect(() => {
    const currentPath = location.pathname;
    const currentPathIndex = filteredPaths.findIndex(
      ({ to }) => `/${to}` === currentPath
    );

    if (currentPathIndex === -1) {
      const firstPath = filteredPaths[0];
      if (firstPath) {
        navigate(firstPath.to);
      }
    }
  }, [filteredPaths, navigate, location.pathname]);

  useEffect(() => {
    const isMediaPlanningRoot =
      location.pathname === `/${routes.mediaPlanning}`;

    if (navType === 'POP' && isMediaPlanningRoot && !handledPopRef.current) {
      handledPopRef.current = true;
      resetPlanStore();
      return;
    }

    handledPopRef.current = false;
  }, [location.pathname, navType]);

  return (
    <Wrapper>
      <Content
        sx={{
          backgroundColor: '#FAFAFA',
        }}
      >
        {!isPlanCreating && (
          <Stack sx={{ mx: '20px', mt: '18px' }}>
            <NavTabs paths={filteredPaths} />
          </Stack>
        )}
        <Stack flexGrow={1}>
          <Outlet />
        </Stack>
      </Content>
    </Wrapper>
  );
}

export default Calculator;
